/**
 * Marketing (Growth) Health — one-glance verdict on the founder's
 * outward-facing traction, mirroring `fundingHealth.js`. Reads only
 * signals already denormalised on the business doc (`crm_rollup`) so
 * the Hub stays a single-fetch page.
 *
 * Four legs (each 0–100), averaged for the overall %:
 *
 *   1. Audience    — total CRM contacts (open + won deals).
 *   2. Reach       — how many distinct segments have generated wins.
 *   3. Conversion  — closed-won deals count.
 *   4. Pipeline    — dollar value of the weighted open pipeline.
 *
 * Ladder colours match Funding Health so the two verdicts stack on the
 * same visual rail:
 *   - ≥ 75  → green  "Strong"
 *   - 50–75 → amber  "Building"
 *   - < 50  → red    "Fragile"
 *   - no plan → grey "—"
 */

export const MARKETING_HEALTH_COLOR = {
  strong: "#059669",
  building: "#F59E0B",
  fragile: "#DC2626",
  unknown: "#9CA3AF",
};

export const MARKETING_HEALTH_LABEL = {
  strong: "Strong",
  building: "Building",
  fragile: "Fragile",
  unknown: "—",
};

function audienceScore(rollup) {
  const total = (rollup?.open_count || 0) + (rollup?.won_count || 0);
  if (total >= 5) return 100;
  if (total >= 1) return 60;
  return 0;
}

function reachScore(rollup) {
  const segments = Array.isArray(rollup?.won_by_segment) ? rollup.won_by_segment.length : 0;
  if (segments >= 2) return 100;
  if (segments === 1) return 60;
  return 20;
}

function conversionScore(rollup) {
  const won = rollup?.won_count || 0;
  const open = rollup?.open_count || 0;
  if (won >= 3) return 100;
  if (won >= 1) return 60;
  if (open > 0) return 30;
  return 0;
}

function pipelineScore(rollup) {
  const wp = rollup?.weighted_pipeline || 0;
  if (wp >= 10000) return 100;
  if (wp >= 1000) return 60;
  if (wp > 0) return 30;
  return 0;
}

/**
 * Compute Marketing Health for a business — returns `{ status: "unknown" }`
 * when the founder has no plan yet, so the Hub tile can degrade cleanly.
 */
export function computeMarketingHealth(business) {
  if (!business?.plan) return { pct: 0, status: "unknown", legs: null };
  const rollup = business.crm_rollup || {};
  const legs = {
    audience: audienceScore(rollup),
    reach: reachScore(rollup),
    conversion: conversionScore(rollup),
    pipeline: pipelineScore(rollup),
  };
  const pct = Math.round(
    (legs.audience + legs.reach + legs.conversion + legs.pipeline) / 4,
  );
  let status;
  if (pct >= 75) status = "strong";
  else if (pct >= 50) status = "building";
  else status = "fragile";
  return { pct, status, legs };
}

// Feb 15 · Marketing Health blends four legs at equal weight (25% each).
// Exported so the strip can render weights per leg — matching the
// pattern established by the Operating Health hero.
export const MARKETING_HEALTH_WEIGHTS = {
  audience: 25,
  reach: 25,
  conversion: 25,
  pipeline: 25,
};

// Rung ladders per leg — used by nextMarketingLever to advertise the
// next concrete action a founder can take to raise their score.
function nextLeverForAudience(score, id) {
  if (score >= 100) return null;
  const next = score === 0 ? 60 : 100;
  const action =
    score === 0
      ? "Add your first CRM contact to build audience"
      : "Grow CRM contacts to 5+ to lock in audience";
  return { action, to: `/business/${id}/crm`, delta: (next - score) / 4 };
}

function nextLeverForReach(score, id) {
  if (score >= 100) return null;
  const next = score === 20 ? 60 : 100;
  const action =
    score === 20
      ? "Tag contacts with a segment so wins map to reach"
      : "Win a second segment to broaden reach";
  return { action, to: `/business/${id}/crm`, delta: (next - score) / 4 };
}

function nextLeverForConversion(score, id) {
  if (score >= 100) return null;
  if (score === 60) return { action: "Close 3+ won deals to prove conversion", to: `/business/${id}/crm`, delta: (100 - 60) / 4 };
  if (score === 30) return { action: "Move a lead from Open to Closed-won", to: `/business/${id}/crm`, delta: (60 - 30) / 4 };
  return { action: "Run a campaign to source your first lead", to: `/business/${id}/campaigns`, delta: (30 - 0) / 4 };
}

function nextLeverForPipeline(score, id) {
  if (score >= 100) return null;
  if (score === 60) return { action: "Grow weighted pipeline past $10k", to: `/business/${id}/crm`, delta: (100 - 60) / 4 };
  if (score === 30) return { action: "Log deal values so pipeline reflects reality", to: `/business/${id}/crm`, delta: (60 - 30) / 4 };
  return { action: "Log an open deal to seed the pipeline", to: `/business/${id}/crm`, delta: (30 - 0) / 4 };
}

const LEG_ORDER = ["audience", "conversion", "pipeline", "reach"];
const LEG_LABELS = {
  audience: "Audience",
  reach: "Reach",
  conversion: "Conversion",
  pipeline: "Pipeline",
};

/**
 * Weakest-leg next-action hint for the Marketing tile. Mirrors the
 * Funding tile's `nextFundingLever` contract.
 */
export function nextMarketingLever(business) {
  if (!business?.plan) return null;
  const { legs } = computeMarketingHealth(business);
  if (!legs) return null;
  const minScore = Math.min(...LEG_ORDER.map((k) => legs[k]));
  if (minScore >= 100) return null;
  const key = LEG_ORDER.find((k) => legs[k] === minScore);
  const id = business.id;
  let lever;
  if (key === "audience") lever = nextLeverForAudience(legs.audience, id);
  else if (key === "conversion") lever = nextLeverForConversion(legs.conversion, id);
  else if (key === "pipeline") lever = nextLeverForPipeline(legs.pipeline, id);
  else lever = nextLeverForReach(legs.reach, id);
  if (!lever) return null;
  return {
    leg: key,
    leg_label: LEG_LABELS[key],
    action: lever.action,
    to: lever.to,
    delta_pct: Math.round(lever.delta),
  };
}
